/**
 * Configuración global de la plataforma.
 *
 * Aquí vive el dominio público con el que se comparten las landings y la
 * versión del sistema que usa el actualizador centralizado.
 */

/** Dominio público definitivo. Nunca se comparte el dominio temporal. */
export const PUBLIC_BASE_URL = 'https://www.sistemaava.com'

/**
 * Versión del sistema. Se compara con la versión guardada de cada empresa
 * para saber si necesita recibir las mejoras centralizadas.
 */
export const SYSTEM_VERSION = 1

/**
 * Modo de publicación. Preparado para crecer sin rehacer la arquitectura:
 *  - 'path'      → sistemaava.com/{slug}            (actual)
 *  - 'subdomain' → {slug}.sistemaava.com            (futuro)
 *  - 'custom'    → dominio propio de cada empresa   (futuro)
 * Al cambiar este valor, todas las URL públicas se recalculan solas.
 */
export type PublishMode = 'path' | 'subdomain' | 'custom'
export const PUBLISH_MODE: PublishMode = 'path'

/** Dominio raíz usado para los futuros subdominios de cliente. */
export const ROOT_DOMAIN = 'sistemaava.com'

/** URL pública de una landing según el modo de publicación activo. */
export function publicLandingUrl(slug: string, customDomain?: string): string {
  const clean = slug.replace(/^\/+|\/+$/g, '')
  if (customDomain) return `https://${customDomain}`
  if (PUBLISH_MODE === 'subdomain') return `https://${clean}.${ROOT_DOMAIN}`
  return `${PUBLIC_BASE_URL}/${clean}`
}

/** URL pública de un formulario independiente. */
export function publicFormUrl(slug: string): string {
  return `${PUBLIC_BASE_URL}/formulario/${slug.replace(/^\/+|\/+$/g, '')}`
}

/** Normaliza un texto libre a un slug válido para una URL. */
export function normalizeSlug(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

/**
 * Normaliza el slug que llega por la URL antes de consultarlo.
 *
 * `useParams()` devuelve el valor ya decodificado, así que una URL compartida
 * con un espacio al final (`/adrian1%20`) entregaba `"adrian1 "` y la consulta
 * `.eq('slug', 'adrian1 ')` no encontraba nada → falso «esta página no
 * existe». Lo mismo con barras sobrantes o mayúsculas al copiar el enlace.
 * Aquí se reduce siempre al slug canónico.
 */
export function normalizePublicSlug(raw: string): string {
  let value = raw ?? ''
  try {
    // Cubre los enlaces doblemente codificados (%2520) de algunos clientes.
    value = decodeURIComponent(value)
  } catch {
    // Si la secuencia no es válida se usa el valor tal cual.
  }
  return value
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .trim()
    .toLowerCase()
}

/**
 * Rutas que necesitan la sesión del panel privado.
 *
 * Todo lo demás —landing por slug, formulario público, página de gracias y el
 * 404— es contenido para visitantes: ahí NO debe restaurarse la sesión ni
 * cargarse el espacio de trabajo (leads, notas, estados, fuentes,
 * organización…), aunque el navegador tenga una sesión abierta en otra
 * pestaña. Las páginas públicas solo consultan la landing o el formulario.
 */
const SESSION_ROUTES = [
  '/app',
  '/login',
  '/registro',
  '/recuperar-contrasena',
  '/restablecer-contrasena',
]

/** true si la ruta pertenece al panel privado o al flujo de acceso. */
export function routeNeedsSession(pathname: string): boolean {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/'
  return SESSION_ROUTES.some(
    (route) => path === route || path.startsWith(`${route}/`),
  )
}

/** Slugs reservados por rutas internas de la aplicación. */
const RESERVED_SLUGS = [
  'app',
  'login',
  'registro',
  'recuperar',
  'landing',
  'formulario',
  'gracias',
  'recuperar-contrasena',
  'restablecer-contrasena',
  'api',
  'www',
]

/** Valida un slug y devuelve el motivo si no es utilizable. */
export function validateSlug(
  slug: string,
  takenSlugs: string[],
): { valid: boolean; reason?: string } {
  if (slug.length < 3) {
    return { valid: false, reason: 'Usa al menos 3 caracteres.' }
  }
  if (RESERVED_SLUGS.includes(slug)) {
    return { valid: false, reason: 'Este enlace está reservado por el sistema.' }
  }
  if (takenSlugs.includes(slug)) {
    return { valid: false, reason: 'Ya existe otra página con este enlace.' }
  }
  return { valid: true }
}
